import { useState, useEffect, useCallback, useRef } from "react";
import { Trash2, Check, Plus, X, Save, Upload, Image as ImageIcon, Link2, GripVertical, ExternalLink, Bold, Italic, Underline as UnderlineIcon, List, History, Clock, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import Cropper from "react-easy-crop";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TipTapUnderline from "@tiptap/extension-underline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function api(token) {
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
}

export { API };

// ========== CROP HELPER ==========
function createCroppedBlob(imageSrc, cropPixels) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = cropPixels.width;
        canvas.height = cropPixels.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, cropPixels.width, cropPixels.height);
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Canvas toBlob failed"));
        }, "image/jpeg", 0.92);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Image load failed"));
    img.src = imageSrc;
  });
}

// ========== IMAGE UPLOADER WITH CROP + GALLERY ==========
export function ImageUploader({ token, currentUrl, onUploaded, label = "Image", aspect = undefined }) {
  const [uploading, setUploading] = useState(false);
  const [urlMode, setUrlMode] = useState(false);
  const [galleryMode, setGalleryMode] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [manualUrl, setManualUrl] = useState("");
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState(null);
  const fileRef = useRef(null);

  const loadGallery = () => {
    api(token).get("/gallery").then((r) => setGalleryImages(r.data)).catch(() => {});
  };

  const onFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onCropComplete = useCallback((_, croppedPixels) => { setCroppedArea(croppedPixels); }, []);

  const uploadCropped = async () => {
    if (!croppedArea || !cropSrc) {
      toast.error("Please adjust the crop area first");
      return;
    }
    setUploading(true);
    try {
      const blob = await createCroppedBlob(cropSrc, croppedArea);
      const formData = new FormData();
      formData.append("file", new File([blob], "cropped.jpg", { type: "image/jpeg" }));
      const { data } = await api(token).post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(`${API}/files/${data.path}`);
      toast.success("Image cropped & uploaded!");
      setCropSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedArea(null);
    } catch (err) {
      console.error("Crop upload error:", err);
      toast.error(err.response?.data?.detail || err.message || "Upload failed");
    }
    setUploading(false);
  };

  const uploadOriginal = async () => {
    if (!cropSrc) return;
    setUploading(true);
    try {
      const res = await fetch(cropSrc);
      const blob = await res.blob();
      const formData = new FormData();
      formData.append("file", new File([blob], "original.jpg", { type: blob.type }));
      const { data } = await api(token).post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(`${API}/files/${data.path}`);
      toast.success("Uploaded!");
      setCropSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedArea(null);
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      <label className="text-zinc-500 text-xs uppercase tracking-wider block">{label}</label>
      {currentUrl && !cropSrc && !galleryMode && (
        <div className="relative w-full h-32 rounded-lg overflow-hidden border border-white/10 bg-gg-black">
          <img src={currentUrl} alt="Current" className="w-full h-full object-cover" />
        </div>
      )}
      {cropSrc && (
        <div className="border border-white/10 rounded-xl overflow-hidden bg-gg-black">
          <div className="relative h-72 sm:h-80 bg-gg-black">
            <Cropper image={cropSrc} crop={crop} zoom={zoom} aspect={aspect || 1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} style={{ containerStyle: { borderRadius: "0.5rem" } }} />
          </div>
          <div className="p-3 border-t border-white/10 space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-zinc-500 text-xs shrink-0">Zoom</span>
              <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-gg-red" />
            </div>
            {aspect && <p className="text-zinc-600 text-[10px]">Aspect ratio locked to {aspect === 1 ? "1:1 (square)" : aspect > 1 ? `${Math.round(aspect * 10) / 10}:1 (landscape)` : `1:${Math.round((1/aspect) * 10) / 10} (portrait)`}</p>}
            <div className="flex gap-2">
              <Button size="sm" onClick={uploadCropped} disabled={uploading} className="bg-gg-red hover:bg-gg-red-hover rounded-lg text-xs font-heading uppercase tracking-wider flex-1">{uploading ? "Uploading..." : "Crop & Upload"}</Button>
              <Button size="sm" onClick={uploadOriginal} disabled={uploading} variant="outline" className="rounded-lg text-xs border-white/10 text-zinc-400 hover:text-white">Original</Button>
              <Button size="sm" onClick={() => { setCropSrc(null); setCrop({ x: 0, y: 0 }); setZoom(1); setCroppedArea(null); }} variant="ghost" className="text-zinc-500 hover:text-white"><X size={14} /></Button>
            </div>
          </div>
        </div>
      )}
      {galleryMode && (
        <div className="border border-white/10 rounded-xl p-3 bg-gg-black space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-zinc-400 text-xs font-heading uppercase tracking-wider">Choose from Gallery</span>
            <Button size="sm" variant="ghost" onClick={() => setGalleryMode(false)} className="text-zinc-500 hover:text-white"><X size={14} /></Button>
          </div>
          {galleryImages.length === 0 ? <p className="text-zinc-600 text-xs py-4 text-center">No images in gallery yet. Upload some in the Gallery tab.</p> : (
            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
              {galleryImages.map((img) => (
                <button key={img.id} onClick={() => { onUploaded(`${API.replace('/api', '')}${img.url}`); setGalleryMode(false); toast.success("Image selected!"); }}
                  className="aspect-square rounded-lg overflow-hidden border border-white/10 hover:border-gg-red transition-colors">
                  <img src={`${API.replace('/api', '')}${img.url}`} alt={img.filename} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {!cropSrc && !galleryMode && (
        <>
          <div className="flex gap-2">
            <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" className="bg-gg-red hover:bg-gg-red-hover rounded-lg text-xs font-heading uppercase tracking-wider flex-1">
              <Upload size={14} className="mr-1.5" /> {uploading ? "..." : "Upload"}
            </Button>
            <Button type="button" onClick={() => { setGalleryMode(true); loadGallery(); }} size="sm" variant="outline" className="rounded-lg text-xs border-white/10 text-zinc-400 hover:text-white">
              <ImageIcon size={14} className="mr-1" /> Gallery
            </Button>
            <Button type="button" onClick={() => setUrlMode(!urlMode)} size="sm" variant="outline" className="rounded-lg text-xs border-white/10 text-zinc-400 hover:text-white">
              <Link2 size={14} />
            </Button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileSelect} />
          {urlMode && (
            <div className="flex gap-2">
              <Input value={manualUrl} onChange={(e) => setManualUrl(e.target.value)} placeholder="Paste image URL..." className="bg-gg-black border-white/10 text-white rounded-lg text-sm h-9 flex-1" />
              <Button size="sm" onClick={() => { if (manualUrl.trim()) { onUploaded(manualUrl.trim()); setManualUrl(""); setUrlMode(false); } }} className="bg-gg-red hover:bg-gg-red-hover rounded-lg text-xs shrink-0">Apply</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ========== RICH TEXT EDITOR ==========
export function RichTextEditor({ value, onChange, label }) {
  const editor = useEditor({
    extensions: [StarterKit, TipTapUnderline],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-invert prose-sm max-w-none min-h-[200px] px-4 py-3 focus:outline-none text-zinc-300 text-sm leading-relaxed",
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  if (!editor) return null;

  const btnCls = (active) =>
    `p-1.5 rounded transition-colors ${active ? "bg-gg-red text-white" : "text-zinc-500 hover:text-white hover:bg-white/10"}`;

  return (
    <div>
      <label className="text-zinc-500 text-xs uppercase tracking-wider mb-1 block">{label}</label>
      <div className="border border-white/10 rounded-lg overflow-hidden bg-gg-black">
        <div className="flex items-center gap-1 px-3 py-2 border-b border-white/10 bg-gg-surface/50">
          <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnCls(editor.isActive("bold"))} title="Bold"><Bold size={14} /></button>
          <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnCls(editor.isActive("italic"))} title="Italic"><Italic size={14} /></button>
          <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btnCls(editor.isActive("underline"))} title="Underline"><UnderlineIcon size={14} /></button>
          <div className="w-px h-4 bg-white/10 mx-1" />
          <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnCls(editor.isActive("bulletList"))} title="Bullet List"><List size={14} /></button>
          <div className="flex-1" />
          <span className="text-zinc-600 text-[10px]">Rich Text</span>
        </div>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

// ========== FIELD ==========
export function Field({ label, value, onChange, type = "text", rows, placeholder }) {
  const cls = "bg-gg-black border-white/10 text-white rounded-lg text-sm";
  return (
    <div>
      <label className="text-zinc-500 text-xs uppercase tracking-wider mb-1 block">{label}</label>
      {rows ? <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} className={`${cls} resize-none`} />
        : <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${cls} h-10`} />}
    </div>
  );
}

// ========== CARD ==========
export function Card({ title, children, onSave, saving, onPublish, publishing, icon: Icon, previewUrl, isDraft, section, token, scheduledAt, onMetaUpdate }) {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduling, setScheduling] = useState(false);

  const loadHistory = async () => {
    if (!section || !token) return;
    setLoadingHistory(true);
    try {
      const { data } = await api(token).get(`/content/${section}/history`);
      setHistory(data);
    } catch { toast.error("Failed to load history"); }
    setLoadingHistory(false);
  };

  const restoreVersion = async (historyId, date) => {
    try {
      await api(token).post(`/content/${section}/restore/${historyId}`);
      toast.success(`Restored to version from ${new Date(date).toLocaleString()}`);
      setShowHistory(false);
      window.location.reload();
    } catch { toast.error("Restore failed"); }
  };

  const handleSchedule = async () => {
    if (!scheduleDate) return;
    setScheduling(true);
    try {
      // Save draft first
      if (onSave) await onSave(true);
      await api(token).post(`/content/${section}/schedule`, { scheduled_publish_at: new Date(scheduleDate).toISOString() });
      toast.success(`Scheduled to publish at ${new Date(scheduleDate).toLocaleString()}`);
      setShowSchedule(false);
      setScheduleDate("");
      if (onMetaUpdate) onMetaUpdate(section, { scheduled_publish_at: new Date(scheduleDate).toISOString() });
    } catch { toast.error("Schedule failed"); }
    setScheduling(false);
  };

  const cancelSchedule = async () => {
    try {
      await api(token).delete(`/content/${section}/schedule`);
      toast.success("Schedule cancelled");
      if (onMetaUpdate) onMetaUpdate(section, { scheduled_publish_at: null });
    } catch { toast.error("Cancel failed"); }
  };

  return (
    <div className="border border-white/10 rounded-xl bg-gg-surface p-5 md:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-white/5 pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          {Icon && <Icon size={16} className="text-gg-red" />}
          <h3 className="font-heading text-sm uppercase tracking-wider font-bold text-white">{title}</h3>
          {isDraft && <span className="text-[10px] px-2 py-0.5 bg-yellow-900/30 text-yellow-400 rounded-full font-heading uppercase tracking-wider">Draft</span>}
          {scheduledAt && (
            <span className="text-[10px] px-2 py-0.5 bg-blue-900/30 text-blue-400 rounded-full font-heading uppercase tracking-wider flex items-center gap-1">
              <CalendarClock size={10} /> {new Date(scheduledAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {section && (
            <Button onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }} size="sm" variant="outline" className="rounded-lg text-xs font-heading uppercase tracking-wider border-white/10 text-zinc-400 hover:text-white">
              <History size={13} className="mr-1.5" /> History
            </Button>
          )}
          {previewUrl && (
            <Button onClick={() => window.open(previewUrl, "_blank")} size="sm" variant="outline" className="rounded-lg text-xs font-heading uppercase tracking-wider border-white/10 text-zinc-400 hover:text-white">
              <ExternalLink size={13} className="mr-1.5" /> Preview
            </Button>
          )}
          {onSave && (
            <Button onClick={() => onSave(false)} disabled={saving} size="sm" variant="outline" className="rounded-lg text-xs font-heading uppercase tracking-wider border-white/10 text-zinc-400 hover:text-white">
              <Save size={13} className="mr-1.5" /> {saving ? "..." : "Save Draft"}
            </Button>
          )}
          {section && (
            <Button onClick={() => setShowSchedule(!showSchedule)} size="sm" variant="outline" className="rounded-lg text-xs font-heading uppercase tracking-wider border-white/10 text-blue-400 hover:text-blue-300">
              <Clock size={13} className="mr-1.5" /> Schedule
            </Button>
          )}
          {onPublish && (
            <Button onClick={onPublish} disabled={publishing} size="sm" className="bg-green-600 hover:bg-green-700 rounded-lg text-xs font-heading uppercase tracking-wider text-white">
              <Check size={13} className="mr-1.5" /> {publishing ? "..." : "Publish"}
            </Button>
          )}
        </div>
      </div>

      {/* Schedule Panel */}
      {showSchedule && (
        <div className="border border-blue-500/20 rounded-lg bg-blue-950/20 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-heading text-xs uppercase tracking-wider text-blue-400 flex items-center gap-1.5"><CalendarClock size={13} /> Schedule Publish</h4>
            <Button size="sm" variant="ghost" onClick={() => setShowSchedule(false)} className="text-zinc-500 hover:text-white"><X size={14} /></Button>
          </div>
          {scheduledAt ? (
            <div className="flex items-center justify-between">
              <p className="text-blue-300 text-xs">Scheduled for: <strong>{new Date(scheduledAt).toLocaleString()}</strong></p>
              <Button size="sm" onClick={cancelSchedule} variant="outline" className="border-red-500/30 text-red-400 hover:text-red-300 rounded-lg text-[10px]">Cancel Schedule</Button>
            </div>
          ) : (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-[10px] uppercase text-zinc-500 block mb-1">Publish Date & Time</label>
                <input type="datetime-local" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full bg-gg-black border border-white/10 text-white rounded-lg h-9 px-3 text-xs" />
              </div>
              <Button size="sm" onClick={handleSchedule} disabled={scheduling || !scheduleDate} className="bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-heading uppercase tracking-wider text-white h-9">
                {scheduling ? "..." : "Set Schedule"}
              </Button>
            </div>
          )}
          <p className="text-zinc-600 text-[10px]">Draft will be saved and auto-published at the scheduled time.</p>
        </div>
      )}

      {showHistory && (
        <div className="border border-white/10 rounded-lg bg-gg-black p-4 space-y-2">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-heading text-xs uppercase tracking-wider text-zinc-400">Version History</h4>
            <Button size="sm" variant="ghost" onClick={() => setShowHistory(false)} className="text-zinc-500 hover:text-white"><X size={14} /></Button>
          </div>
          {loadingHistory && <p className="text-zinc-600 text-xs">Loading...</p>}
          {!loadingHistory && history.length === 0 && <p className="text-zinc-600 text-xs">No history yet.</p>}
          <div className="max-h-52 overflow-y-auto space-y-1.5">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between p-2.5 rounded-lg bg-gg-surface border border-white/5 hover:border-white/10 transition-colors group">
                <div>
                  <p className="text-zinc-300 text-xs">{new Date(h.created_at).toLocaleString()}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${h.action === "publish" ? "bg-green-900/30 text-green-400" : h.action === "scheduled_publish" ? "bg-blue-900/30 text-blue-400" : h.action === "pre_restore" ? "bg-blue-900/30 text-blue-400" : "bg-zinc-800 text-zinc-500"}`}>
                    {h.action === "publish" ? "Published" : h.action === "scheduled_publish" ? "Scheduled" : h.action === "pre_restore" ? "Pre-restore" : "Draft"}
                  </span>
                </div>
                <Button size="sm" onClick={() => restoreVersion(h.id, h.created_at)} className="opacity-0 group-hover:opacity-100 transition-opacity bg-gg-red hover:bg-gg-red-hover rounded-lg text-[10px] font-heading uppercase tracking-wider h-7 px-3">Restore</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}
